import { useCallback } from 'react';
import { useCancelStringGroupTranslationMutation } from 'src/generated/graphql';
import {
  DeniedNamespace,
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * Resource id of the copy shown when the failure carries nothing worth repeating. Every other
 * case surfaces the service's own wording, which is upstream content.
 */
export const CANCEL_FALLBACK_MESSAGE_KEY = 'sgd-cancel-error-generic';

interface CancelResult {
  success: boolean;
  version?: number;
  /**
   * User-facing failure text — the service's own message whenever it sent one, so
   * the caller never has to reword a refusal.
   */
  error?: string;
  /**
   * True for an authorization refusal. Reported separately from `error` because
   * the two need different treatments: a refusal is terminal and shows the
   * service's wording alone, whereas a fault is worth a retry.
   */
  accessDenied?: boolean;
  /**
   * The namespace a refusal named, which the modal turns into a Bindle link.
   *
   * Kept out of `error` because that string is rendered verbatim, so structured
   * detail must not be folded into service-owned prose. Absent for every
   * non-refusal and for a refusal the service sent no detail with.
   */
  deniedNamespace?: DeniedNamespace;
}

interface UseCancelTranslationResult {
  cancel: (reason: string) => Promise<CancelResult>;
  loading: boolean;
}

/**
 * Cancels an in-flight translation for a string group via the
 * CancelStringGroupTranslation GraphQL mutation.
 */
export function useCancelTranslation(
  namespaceId: string,
  stringGroupId: string,
  version: number,
): UseCancelTranslationResult {
  const [mutate, { loading }] = useCancelStringGroupTranslationMutation();
  const { t } = useTranslation();

  const cancel = useCallback(
    async (reason: string): Promise<CancelResult> => {
      try {
        const { data } = await mutate({
          variables: {
            namespaceId,
            stringGroupId,
            version: version.toString(),
            reason,
          },
        });

        return {
          success: true,
          version: data?.cancelStringGroupTranslation.version,
        };
      } catch (e) {
        const accessDenied = isAccessDenied(e);
        // A refusal is passed through verbatim: the service, not the UI, decides
        // how much to say about a resource the caller may not see. Read off the
        // MATCHED error rather than the ApolloError aggregate, whose `message`
        // concatenates every error in the response.
        const message = accessDenied
          ? getAccessDeniedMessage(e)
          : e instanceof Error
            ? e.message
            : '';
        return {
          success: false,
          error: message || t(CANCEL_FALLBACK_MESSAGE_KEY),
          accessDenied,
          // Read off the same matched error as the message, so the link and the
          // sentence above it always describe one refusal.
          deniedNamespace: accessDenied
            ? getAccessDeniedNamespace(e)
            : undefined,
        };
      }
    },
    [mutate, namespaceId, stringGroupId, version, t],
  );

  return { cancel, loading };
}
